import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Lock } from "lucide-react";
import { PERSONAS } from "@/data/content";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { FadeUp, SectionHeading } from "@/components/Reveal";
import { memberApi } from "./MembershipPage";

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function MemberGuidePage() {
  const { audience } = useParams();
  const persona = PERSONAS[audience];
  const [entries, setEntries] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    setEntries(null);
    setDenied(false);
    memberApi()
      .get(`/guides/${audience}/content`)
      .then((res) => setEntries(res.data))
      .catch((err) => {
        if (err.response?.status === 403 || err.response?.status === 401) setDenied(true);
        else setDenied(true);
      });
  }, [audience]);

  const byLetter = useMemo(() => {
    const map = {};
    for (const e of entries || []) (map[e.letter] = map[e.letter] || []).push(e);
    return map;
  }, [entries]);

  if (!persona) return null;

  return (
    <div className="bg-jawa-ivory" data-testid={`member-guide-${audience}`}>
      <Nav />
      <main className="mx-auto max-w-5xl px-6 pb-24 pt-32 lg:px-10 lg:pt-40">
        <FadeUp>
          <SectionHeading overline={`Members-only · ${persona.title}`} title={persona.guideCta} />
        </FadeUp>

        {denied ? (
          <FadeUp className="mt-12 border border-jawa-gold/50 bg-white p-10 text-center" data-testid="guide-locked">
            <Lock className="mx-auto h-6 w-6 text-jawa-gold" />
            <h2 className="mt-4 font-serif text-2xl text-jawa-espresso">This collection is members-only</h2>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-jawa-espresso/65">
              Sign in with an active JÀWÀ {persona.title.replace("The ", "")} Membership to read the full collection.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to={`/membership/${audience}`}
                data-testid="locked-membership-link"
                className="bg-jawa-coral px-6 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-white hover:bg-jawa-coral/90"
              >
                View Membership
              </Link>
              <Link
                to="/members"
                data-testid="locked-signin-link"
                className="border border-jawa-espresso/25 px-6 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-espresso hover:border-jawa-turquoise"
              >
                Member sign-in
              </Link>
            </div>
          </FadeUp>
        ) : entries === null ? (
          <p className="mt-16 text-sm uppercase tracking-[0.25em] text-jawa-espresso/50">Loading your collection…</p>
        ) : (
          <>
            <div className="sticky top-[72px] z-30 mt-10 border-y border-jawa-stone bg-jawa-ivory/95 py-4 backdrop-blur-md" data-testid="member-letter-index">
              <div className="flex flex-wrap gap-x-3 gap-y-2">
                {LETTERS.map((L) => {
                  const has = Boolean(byLetter[L]?.length);
                  return (
                    <button
                      key={L}
                      disabled={!has}
                      onClick={() => document.getElementById(`mg-${L}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                      data-testid={`member-letter-${L}`}
                      className={`text-sm uppercase ${
                        has
                          ? "cursor-pointer font-bold text-jawa-turquoise underline decoration-jawa-turquoise/40 underline-offset-4 hover:text-jawa-espresso"
                          : "cursor-default text-jawa-nearblack/30"
                      }`}
                    >
                      {L}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-12 space-y-14" data-testid="member-entries">
              {LETTERS.filter((L) => byLetter[L]?.length).map((L) => (
                <section key={L} id={`mg-${L}`} className="scroll-mt-40" data-testid={`member-letter-section-${L}`}>
                  <span className="border-b border-jawa-gold/40 pb-3 font-serif text-5xl font-medium text-jawa-gold">{L}</span>
                  <div className="mt-6 space-y-6">
                    {byLetter[L].map((e) => (
                      <article key={e.title} className="border border-jawa-stone bg-white p-7" data-testid={`entry-${e.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        <h3 className="font-serif text-2xl font-medium tracking-tight text-jawa-espresso">{e.title}</h3>
                        <p className="mt-3 text-sm leading-relaxed text-jawa-espresso/75">{e.body}</p>
                      </article>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            <p className="mt-16 border-t border-jawa-stone pt-8 text-xs leading-relaxed text-jawa-espresso/50">
              JÀWÀ A–Z collections provide practical orientation and curated information — not regulated legal,
              immigration, financial or investment advice. Where a decision carries legal or financial weight,
              consult qualified professionals.
            </p>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
